import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from 'papaparse';

function SettlementReport() {
  const { settlementReportsColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [summary, setSummary] = useState([]);

  // Function to get today's date at midnight in AEST timezone
  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of the day in local time
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000); // Convert to local midnight in AEST
  };

  // Initialize lastMonthDateRange to today's date at local midnight
  const [lastMonthDateRange, setLastMonthDateRange] = useState([
    getTodayDateAtMidnight().toISOString().slice(0, 10),
    getTodayDateAtMidnight().toISOString().slice(0, 10)
  ]);
  
  const api = useApi();

  // Format date to 'yyyy-MM-dd' and adjust to AEST timezone
  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.error("Invalid date provided:", date);
      return '';
    }
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  const getSettlementReport = (dateRange) => {
    if (!dateRange || dateRange.length !== 2) return;

    const fromDate = new Date(dateRange[0]);
    const toDate = new Date(dateRange[1]);

    const data = {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    };

    api.post("reports/settlementreportlist", data)
      .then((response) => {
        if (response.data.statusCodeValue === 200) {
          setSummary(response.data.body);
        } else {
          const toastId = toast.error("No Record Found", { autoClose: false });
          setTimeout(() => toast.dismiss(toastId), 1000);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        const toastId = toast.error("No Record Found", { autoClose: false });
        setTimeout(() => toast.dismiss(toastId), 1000);
      });
  };

  const generateCSV = () => {
    if (summary.length === 0) {
      return '';
    }

    const headers = settlementReportsColumns.map(column => column.Header);

    const summaryWithUppercaseColumns = summary.map(item => {
      return settlementReportsColumns.map(column => {
        const accessor = column.accessor;
        const value = typeof accessor === 'function' ? accessor(item) : item[accessor];
        return value;
      });
    });

    const csvData = [headers, ...summaryWithUppercaseColumns];

    const csv = Papa.unparse(csvData, {
      quotes: true,
    });

    return csv;
  };

  const handleDownloadCSV = () => {
    if (summary.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false });
      setTimeout(() => toast.dismiss(toastId), 1000);
      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `SettlementReport_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    if (lastMonthDateRange !== null) {
      getSettlementReport(lastMonthDateRange);
    }
  }, [lastMonthDateRange]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3} textAlign={"center"}>
                <DateRangePicker
                  disabledDate={afterToday()}
                  ranges={predefinedRanges}
                  onChange={(dateRange) => {
                    if (dateRange && dateRange.length === 2) {
                      setLastMonthDateRange(dateRange);
                      getSettlementReport(dateRange);
                    }
                  }}
                  format="yyyy-MM-dd"
                  value={lastMonthDateRange.length ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] : []}
                />
              </MDBox>
            </Grid>
            <MDButton
              variant="gradient"
              onClick={handleDownloadCSV}
              color="info"
              style={{ marginRight: "24px", marginBottom: "12px" }}
            >
              Export
            </MDButton>
            <DataTable
              table={{ columns: settlementReportsColumns, rows: summary }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SettlementReport;
